/**
 * Utility functions for date handling
 * SE-Trace: Implements REQ-FE-001-MINIFICATION-SAFETY, REQ-FE-003-ERROR-BOUNDARIES
 */
import { format, isValid } from 'date-fns';

// Date error tracking for monitoring
const dateErrors: Array<{ timestamp: Date; operation: string; input: any; error: string }> = [];

const logDateError = (operation: string, input: any, error: any) => {
  const errorEntry = {
    timestamp: new Date(),
    operation,
    input: JSON.stringify(input),
    error: error?.message || error
  };
  
  dateErrors.push(errorEntry);
  
  // Keep only last 100 errors
  if (dateErrors.length > 100) {
    dateErrors.shift();
  }
  
  console.error('[Date Error]', errorEntry);
};

// Expose for debugging
if (typeof window !== 'undefined') {
  (window as any).__dateErrors = dateErrors;
}

interface FirestoreTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

/**
 * Convert a Firestore timestamp or ISO string to a Date object
 */
export function parseTimestamp(timestamp: any): Date | null {
  try {
    if (!timestamp) {
      return null;
    }
    
    // CRITICAL: Handle empty objects {} - these are invalid timestamps
    if (typeof timestamp === 'object' && timestamp !== null && !(timestamp instanceof Date)) {
      // Check if object is empty (no keys) or only has empty/undefined values
      const keys = Object.keys(timestamp);
      if (keys.length === 0) {
        // Empty object - not a valid timestamp
        return null;
      }
    }
    
    // Handle Firebase SDK Timestamp objects with toDate() method
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // Handle Firestore REST API timestamp structure { timestampValue: "..." }
    if (timestamp.timestampValue !== undefined) {
      const date = new Date(timestamp.timestampValue);
      return isNaN(date.getTime()) ? null : date;
    }
    
    // Handle Firestore REST API timestamp structure { stringValue: "..." }
    if (timestamp.stringValue !== undefined) {
      const date = new Date(timestamp.stringValue);
      return isNaN(date.getTime()) ? null : date;
    }
    
    // Handle string dates
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }
    
    // Handle Firestore timestamp with _seconds
    if (timestamp._seconds !== undefined) {
      return new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
    }
    
    // Handle Firestore timestamp with seconds (no underscore)
    if (timestamp.seconds !== undefined) {
      return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    }
    
    // Handle already Date objects
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // Handle numeric timestamps (milliseconds)
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    // Log unhandled cases for debugging (only if not empty object)
    if (typeof timestamp === 'object' && timestamp !== null && Object.keys(timestamp).length > 0) {
      logDateError('parseTimestamp-unhandled', timestamp, 'Unhandled timestamp format');
    }
    return null;
    
  } catch (error) {
    logDateError('parseTimestamp', timestamp, error);
    return null;
  }
}

/**
 * Format a date for display
 */
export function formatDate(date: Date | string | FirestoreTimestamp | undefined | null, locale = 'de-DE'): string {
  try {
    // CRITICAL FIX: Handle empty objects {} - they are truthy but invalid
    // Check for empty objects BEFORE calling parseTimestamp() to prevent errors
    if (!date || (typeof date === 'object' && date !== null && !(date instanceof Date) && Object.keys(date).length === 0)) {
      return '';
    }
    
    const dateObj = date instanceof Date ? date : parseTimestamp(date);
    if (!dateObj || !isValid(dateObj)) {
      logDateError('formatDate-invalid', date, 'Invalid date for formatting');
      return '';
    }
    
    return dateObj.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    logDateError('formatDate', date, error);
    return '';
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string | FirestoreTimestamp | undefined): string {
  try {
    // CRITICAL FIX: Handle empty objects {} - they are truthy but invalid
    // Check for empty objects BEFORE calling parseTimestamp() to prevent errors
    if (!date || (typeof date === 'object' && date !== null && !(date instanceof Date) && Object.keys(date).length === 0)) {
      return '';
    }
  
  const dateObj = date instanceof Date ? date : parseTimestamp(date);
  if (!dateObj) return '';
  
  const now = new Date();
  const diff = now.getTime() - dateObj.getTime();
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  
  return formatDate(dateObj);
  } catch (error) {
    logDateError('formatRelativeTime', date, error);
    return '';
  }
}

/**
 * Safely format a date with date-fns
 */
export function safeFormatDate(date: string | Date | FirestoreTimestamp | null | undefined, formatStr: string = 'dd.MM.yyyy'): string {
  // CRITICAL FIX: Handle empty objects {} - they are truthy but invalid
  // Check for empty objects BEFORE calling parseTimestamp() to prevent errors
  if (!date || (typeof date === 'object' && date !== null && !(date instanceof Date) && Object.keys(date).length === 0)) {
    return '-';
  }
  
  try {
    const dateObj = date instanceof Date ? date : parseTimestamp(date);
    
    if (!dateObj || !isValid(dateObj)) {
      logDateError('safeFormatDate-invalid', date, 'Invalid date for safe formatting');
      return '-';
    }
    
    return format(dateObj, formatStr);
  } catch (error) {
    logDateError('safeFormatDate', date, error);
    return '-';
  }
}
/** Shared Berlin calendar/clock for cockpit dates, timeline and midnight rollover. */
export function berlinDateTime(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)!.value;
  return { day: `${value('year')}-${value('month')}-${value('day')}`, clock: `${value('hour')}:${value('minute')}`,
    minutes: Number(value('hour')) * 60 + Number(value('minute')) };
}

export function nextBerlinMidnight(now: Date) {
  const next = new Date(`${berlinDateTime(now).day}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  // At UTC midnight Berlin is 01/02:00, before any DST transition that night.
  return next.getTime() - berlinDateTime(next).minutes * 60_000;
}

/** Task creation time, always in the Berlin calendar. */
export function taskCreatedTime(value: string, now = new Date()) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '–';
  const local = berlinDateTime(date);
  return `${local.day === berlinDateTime(now).day ? '' : `${local.day.slice(8, 10)}.${local.day.slice(5, 7)}. `}${local.clock}`;
}

export function taskExportDate(value: string, scheduledClock?: string) {
  if (scheduledClock !== undefined) return `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)} ${scheduledClock}`;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const local = berlinDateTime(date);
  return `${local.day.slice(8, 10)}.${local.day.slice(5, 7)}.${local.day.slice(0, 4)} ${local.clock}`;
}

/** Convert a structured Berlin wall clock, independently of the browser's time zone. */
export function berlinLocalDateTime(day: string, clock: string): Date {
  const wall = Date.parse(`${day}T${clock}:00Z`);
  const offset = (instant: number) => {
    const local = berlinDateTime(new Date(instant));
    return Date.parse(`${local.day}T${local.clock}:00Z`) - instant;
  };
  const candidate = wall - offset(wall);
  return new Date(wall - offset(candidate));
}

/** ISO calendar days ("2026-10-28") as German dd.mm.yyyy; any other text is returned unchanged. */
export function germanDay(value: string | null | undefined): string {
  const iso = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : value ?? '';
}
/** Email timestamps use the same zone for rows and details, including SDK timestamps. */
export function formatEmailDateTime(value: unknown): string {
  const date = parseTimestamp(value);
  if (!date || !Number.isFinite(date.getTime())) return 'Unbekannt';
  return new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}
